import { acceptHMRUpdate, defineStore , storeToRefs} from 'pinia'
import { reactive, ref } from 'vue'
import { getEventOrganizerByIdAndPartitionKeyApi } from '@/api'
import { useErrorStore } from '@/stores/errorStore'
import { useUserStore } from '@/stores/userStore'

import { createEventOrganizer, deleteEventOrganizerById, getEventOrganizersBatch, updateEventOrganizer } from '@/api'
import type { EventOrganizer, ItemReference } from '@/types'
import { useAuthenticationStore } from './useAuthenticationStore'

// BacklogTrace: HR-001
function matchesOrganizerIdentity(
    organizer: EventOrganizer,
    id: string,
    partitionKey?: string | null
) {
    if (partitionKey && organizer.partitionKey !== partitionKey) {
        return false
    }

    return String(organizer.id) === String(id)
}

export interface EventOrganizerStore {
    eventOrganizers: EventOrganizer[]
    getEventOrganizer(id: string, partitionKey?: string | null): EventOrganizer | undefined
    getEventOrganizerByIdAndPartitionKey(id: string, partitionKey:string): Promise<EventOrganizer>
    addNewEventOrganizer(eventOrganizer: EventOrganizer): Promise<EventOrganizer>
    loadEventOrganizersForUser(): Promise<void>
    deleteEventOrganizer(eo: EventOrganizer): Promise<void>
    updateExistingEventOrganizer(organizerToUpdate: EventOrganizer): Promise<void>
}

export const useEventOrganizerStore = defineStore<'eventOrganizer', EventOrganizerStore>(
    'eventOrganizer',
    function (): EventOrganizerStore {
        const userStore = useUserStore()
        const { user } = storeToRefs(userStore)
        const authStore = useAuthenticationStore()
        const errorStore = useErrorStore()
        const eventOrganizers = reactive<EventOrganizer[]>([])
        const lastLoadedOrganizersKey = ref('')
        let loadEventOrganizersPromise: Promise<void> | null = null
        let loadingOrganizersKey = ''
    
        async function loadEventOrganizersForUser(force = false) {
            await userStore.loadUser()

            const organizers = user.value.organizerFor ?? []
            const currentUserId = authStore.userId as string | null
            const organizersKey = currentUserId
                ? `${currentUserId}:${organizers
                    .map((organizer) => `${organizer.partitionKey}:${organizer.id}`)
                    .sort()
                    .join('|')}`
                : ''

            if (!currentUserId) {
                eventOrganizers.length = 0
                lastLoadedOrganizersKey.value = ''
                return
            }

            if (!force && organizersKey === lastLoadedOrganizersKey.value) {
                return
            }

            if (!force && loadEventOrganizersPromise && loadingOrganizersKey === organizersKey) {
                await loadEventOrganizersPromise
                return
            }

            loadingOrganizersKey = organizersKey
            loadEventOrganizersPromise = (async () => {
                if (!organizers.length) {
                    eventOrganizers.length = 0
                    lastLoadedOrganizersKey.value = organizersKey
                    return
                }

                const loadedOrganizers = await getEventOrganizersBatch(organizers)
                const uniqueOrganizers = loadedOrganizers.filter(
                    (eventOrganizer, index, organizerList) =>
                        organizerList.findIndex(
                            (x) =>
                                x.id === eventOrganizer.id
                                && x.partitionKey === eventOrganizer.partitionKey
                        ) === index
                )

                eventOrganizers.length = 0
                eventOrganizers.push(...uniqueOrganizers)
                lastLoadedOrganizersKey.value = organizersKey
            })()

            try {
                await loadEventOrganizersPromise
            } finally {
                loadEventOrganizersPromise = null
                loadingOrganizersKey = ''
            }
        }

        function getEventOrganizer(id: string, partitionKey?: string | null): EventOrganizer | undefined {
            return eventOrganizers.find((x) => matchesOrganizerIdentity(x, id, partitionKey))
        }
        
        async function getEventOrganizerByIdAndPartitionKey(id: string, partitionKey:string) : Promise<EventOrganizer> {
            
            return await  getEventOrganizerByIdAndPartitionKeyApi(id, partitionKey);
        }


        async function addNewEventOrganizer(organizerToAdd: EventOrganizer) {
            try {
                const newOrganizer = await createEventOrganizer(authStore.userId as string, organizerToAdd)
                eventOrganizers.push(newOrganizer as EventOrganizer)
                lastLoadedOrganizersKey.value = ''
                await userStore.loadUser(true)
                return newOrganizer
            } catch (e) {
                console.error(e)
                errorStore.error.message = 'En fejl er opstået'
                errorStore.error.show = true
                throw e
            }
        }
        async function updateExistingEventOrganizer(organizerToUpdate: EventOrganizer) {
            try {
                const updatedOrganizer = await updateEventOrganizer(organizerToUpdate)
                const existingOrganizerIndex = eventOrganizers.findIndex(
                    (org) =>
                        org.id === updatedOrganizer.id
                        && org.partitionKey === updatedOrganizer.partitionKey
                )

                if (existingOrganizerIndex !== -1) {
                    eventOrganizers.splice(existingOrganizerIndex, 1, updatedOrganizer)
                }
                lastLoadedOrganizersKey.value = ''
            } catch (e) {
                console.error(e)
                errorStore.error.message = 'En fejl er opstået'
                errorStore.error.show = true
            }
        }
        let deleteEventOrganizerPromise: Promise<void> | null = null

        async function deleteEventOrganizer(eo: EventOrganizer): Promise<void> {
            if (deleteEventOrganizerPromise) {
                return deleteEventOrganizerPromise
            }

            deleteEventOrganizerPromise = (async () => {
                await deleteEventOrganizerById(eo.id, eo.partitionKey, authStore.userId as string)
                lastLoadedOrganizersKey.value = ''
                await loadEventOrganizersForUser(true)
            })()

            try {
                await deleteEventOrganizerPromise
            } finally {
                deleteEventOrganizerPromise = null
            }
        }

       

        return {
            addNewEventOrganizer,
            deleteEventOrganizer,
            eventOrganizers,
            getEventOrganizer,
            getEventOrganizerByIdAndPartitionKey,
            loadEventOrganizersForUser,
            updateExistingEventOrganizer
        }
    }
)
// make sure to pass the right store definition, `useAuth` in this case.
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useEventOrganizerStore, import.meta.hot))
}
